import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * Scroll handling for list/detail pages that show one pane at a time on
 * phones: opening an item starts at the top of the page, and going back
 * returns the list to where it was scrolled.
 *
 * Put `containerRef` on an element inside the page, and call
 * `rememberListScroll` just before an item opens (e.g. onClickCapture on the
 * list). `showingDetail` should only be true on phones.
 */
export const useListDetailScroll = <T extends HTMLElement>(showingDetail: boolean) => {
    const containerRef = useRef<T>(null);
    const listScrollTopRef = useRef(0);

    useLayoutEffect(() => {
        const scroller = containerRef.current?.closest('.main-content');
        if (!scroller) return;
        scroller.scrollTop = showingDetail ? 0 : listScrollTopRef.current;
    }, [showingDetail]);

    const rememberListScroll = useCallback(() => {
        listScrollTopRef.current = containerRef.current?.closest('.main-content')?.scrollTop ?? 0;
    }, []);

    return { containerRef, rememberListScroll };
};
